import { useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import GooglePlacesInput from "./GooglePlacesInput";
import { REASON_OPTIONS } from "../lib/situations";
import { ArrowRight, CheckCircle2, DollarSign, Phone } from "lucide-react";

const inputBase =
  "w-full h-14 px-4 rounded-md border-2 border-[var(--border-soft)] focus:border-[var(--green-500)] focus:outline-none text-lg bg-white";
const labelBase =
  "block text-xs sm:text-sm font-semibold uppercase tracking-wide text-navy-800 mb-2";

const CONSENT_TRANSACTIONAL_TEXT =
  "I consent to receive non-marketing text messages from Aspire Collective Partners LLC regarding my property inquiry and transaction updates. Message frequency varies. Message & data rates may apply. Reply HELP for assistance, reply STOP to opt out.";
const CONSENT_MARKETING_TEXT =
  "I consent to receive marketing text messages from Aspire Collective Partners LLC regarding promotions, offers, and real estate opportunities. Message frequency varies. Message & data rates may apply. Reply HELP for assistance, reply STOP to opt out.";

export default function LeadFormInline({ sourcePage = "home", situation = "" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    estimated_value: "",
    reason: "",
    consent_transactional_sms: false,
    consent_marketing_sms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(null); // holds server response after success

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Please enter your full name.");
    if (form.phone.replace(/\D/g, "").length < 10)
      return setErr("Enter a valid U.S. phone number.");
    if (!form.address.trim()) return setErr("Please enter your property address.");
    if (!form.reason) return setErr("Please select a reason for selling.");

    setSubmitting(true);
    try {
      const { data } = await client.post("/leads", {
        ...form,
        situation, // preserves situation-page context if present
        source_page: sourcePage,
      });
      setSubmitted(data);
    } catch (e2) {
      setErr("Something went wrong — please try again or call (614) 966-4283.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="bg-white rounded-lg border-2 border-money shadow-md p-8 text-center"
        data-testid="lead-form-success"
      >
        <div className="inline-flex bg-money text-white rounded-full h-14 w-14 items-center justify-center mb-4">
          <CheckCircle2 size={28} />
        </div>
        <h3 className="font-display text-2xl text-navy">
          Thank you, {submitted.name?.split(" ")[0] || "there"}!
        </h3>
        <p className="mt-3 text-navy-800">
          We received your request for <strong>{submitted.address}</strong>. A team member
          from Aspire Collective Partners LLC will call you within <strong>24 hours</strong> with your
          no-obligation cash offer.
        </p>
        <a
          href="tel:+16149664283"
          className="mt-6 inline-flex items-center gap-2 h-12 px-5 rounded-md bg-money text-white font-bold"
          data-testid="success-call-btn"
        >
          <Phone size={16} /> Or call now: (614) 966-4283
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-lg border-2 border-[var(--border-soft)] shadow-md p-6 sm:p-8 space-y-4"
      data-testid="hero-lead-form"
    >
      <div className="flex items-center gap-2 text-money font-bold uppercase tracking-widest text-xs mb-1">
        <DollarSign size={14} /> Request a Cash Offer
      </div>

      <div>
        <label className={labelBase} htmlFor="lead-name">Full Name</label>
        <input
          id="lead-name"
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Jane Doe"
          className={inputBase}
          data-testid="hero-name-input"
        />
      </div>

      <div>
        <label className={labelBase} htmlFor="lead-phone">Phone Number</label>
        <input
          id="lead-phone"
          type="tel"
          autoComplete="tel"
          value={form.phone}
          onChange={(e) => update({ phone: e.target.value })}
          placeholder="(555) 123-4567"
          className={inputBase}
          data-testid="hero-phone-input"
        />
      </div>

      <div>
        <label className={labelBase} htmlFor="lead-address">Property Address</label>
        <GooglePlacesInput
          value={form.address}
          onChange={({ address, city, state, zip }) =>
            update({
              address: address ?? form.address,
              ...(city !== undefined && { city }),
              ...(state !== undefined && { state }),
              ...(zip !== undefined && { zip }),
            })
          }
          placeholder="123 Main St, Anytown, USA"
          testId="hero-address-input"
        />
      </div>

      <div>
        <label className={labelBase} htmlFor="lead-value">Estimated Property Value</label>
        <input
          id="lead-value"
          type="text"
          inputMode="numeric"
          value={form.estimated_value}
          onChange={(e) => update({ estimated_value: e.target.value })}
          placeholder="$250,000"
          className={inputBase}
          data-testid="hero-value-input"
        />
      </div>

      <div>
        <label className={labelBase} htmlFor="lead-reason">Reason for Selling</label>
        <select
          id="lead-reason"
          value={form.reason}
          onChange={(e) => update({ reason: e.target.value })}
          className={`${inputBase} appearance-none cursor-pointer`}
          data-testid="hero-reason-select"
        >
          <option value="" disabled>Select a reason…</option>
          {REASON_OPTIONS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* SMS Consent checkboxes removed per user request. Backend still accepts
          consent_transactional_sms=false/consent_marketing_sms=false defaults. */}

      {err && (
        <p className="text-sm text-red-600" data-testid="hero-form-error">{err}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full h-16 bg-money text-white text-lg sm:text-xl font-bold rounded-md flex items-center justify-center gap-2 hover:brightness-95 transition disabled:opacity-70 shadow-lg"
        data-testid="hero-submit-btn"
      >
        {submitting ? "Working…" : (<>Get My Cash Offer <ArrowRight size={22} /></>)}
      </button>
      <p className="text-xs text-muted text-center">
        No fees • No repairs • Flexible closing • Cash offer in 24 hours
      </p>
      <p className="text-[11px] text-muted text-center" data-testid="legal-links">
        <Link to="/privacy" className="hover:text-money underline-offset-2 hover:underline" data-testid="privacy-link">Privacy Policy</Link>
        <span className="mx-2 text-muted/60">|</span>
        <Link to="/terms" className="hover:text-money underline-offset-2 hover:underline" data-testid="terms-link">Terms and Conditions</Link>
      </p>
    </form>
  );
}
